import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

import { useSingleNotification } from '@hooks/operator/useBackgroundJobNotification';
import { message, Modal } from 'antd';
import printJS from 'print-js';

import i18n from '@i18n/i18n';
import { translateErrorInterpolations } from '@utils/translateErrorInterpolations';

import { QCFlowEvents } from './qcFlowMachine';

import { shipmentService } from '@services/operator/shipmentService';

const QcShipmentContext = createContext();

export const QcShipmentProvider = ({ children, triggerQcFlowEvent, onOrderComplete, onPrintError }) => {
  // 列印 Job ID（用於監聽 PrintLabelWorker）
  const [printJobId, setPrintJobId] = useState(null);

  // 使用 ref 存儲當前出貨單（用於 print job 完成時關閉出貨單）
  const currentQcFulfillmentRef = useRef(null);

  // ✅ 使用 useSingleNotification 監聽正式列印的 print job
  useSingleNotification(printJobId, {
    onCompleted: useCallback((data) => {
      console.log('[QcShipmentContext] Print job completed:', data);
      message.success('託運單產生完成');

      // ✅ 處理列印結果：可能是 pdf_url、logist_pdf_path 或 html
      const resultData = data.data;
      if (resultData) {
        // ✅ 檢查 PDF URL (logist_pdf_path 或 pdf_url) - 使用 print-js 列印
        const pdfUrl = resultData.logist_pdf_path || resultData.pdf_url;

        if (pdfUrl && pdfUrl.startsWith('https://')) {
          console.log('[QcShipmentContext] Triggering print-js for PDF:', pdfUrl);

          try {
            printJS({
              printable: pdfUrl,
              type: 'pdf',
              showModal: true,
              modalMessage: i18n.t('operator.qc.print_label.loading_pdf'),
              onError: (error) => {
                console.error('[QcShipmentContext] ❌ print-js error:', error);
                const errorDetail = error ? `${error}` : i18n.t('operator.qc.print_label.label_load_failed');
                message.error(`${i18n.t('operator.qc.print_label.failed')}：${errorDetail}`);
              },
              onLoadingEnd: () => {
                console.log('[QcShipmentContext] ✅ PDF loaded, print dialog should open');
              }
            });

            console.log('[QcShipmentContext] print-js initiated');
          } catch (err) {
            console.error('[QcShipmentContext] ❌ print-js exception:', err);
            message.error(`${i18n.t('operator.qc.print_label.failed')}：${err.message}`);
          }
        } else if (resultData.shipment_id) {
          // execute_html: 先同步開 about:blank popup 保留 user gesture（避免 async 後被 popup blocker 擋），
          // 再呼叫 API 取 signed URL 並 navigate 過去。
          console.log('[QcShipmentContext] Opening execute_html via signed URL');
          const popup = window.open('about:blank', '_blank');
          if (!popup) {
            message.error(i18n.t('common.error.popup_blocked', { defaultValue: 'Popup 被瀏覽器擋下' }));
            if (onPrintError) onPrintError('popup_blocked');
          } else {
            shipmentService.executeHtmlUrl(resultData.shipment_id).then((res) => {
              if (res.success && res.data?.url) {
                popup.location = res.data.url;
              } else {
                popup.close();
                const errorCode = res.message || 'shipment_failed';
                const interpolations = res.interpolations || {};
                message.error(i18n.t(`common.error.${errorCode}`, { ...interpolations, defaultValue: i18n.t('common.error.operation_failed') }));
                if (onPrintError) onPrintError(errorCode, interpolations);
              }
            }).catch((err) => {
              popup.close();
              console.error('[QcShipmentContext] executeHtmlUrl failed:', err);
              message.error(i18n.t('common.error.network_error'));
              if (onPrintError) onPrintError('network_error');
            });
          }
        } else if (resultData.html) {
          // 舊 worker 版本漏送 shipment_id；避免分支靜默略過卡住 QC 狀態機
          console.warn('[QcShipmentContext] execute_html response missing shipment_id, cannot open label');
          message.error(i18n.t('common.error.operation_failed'));
          if (onPrintError) onPrintError('missing_shipment_id');
        }
      }

      // ✅ 列印成功後，自動呼叫完成出貨單回調
      if (currentQcFulfillmentRef.current && currentQcFulfillmentRef.current.id && onOrderComplete) {
        console.log('[QcShipmentContext] Print succeeded, now closing fulfillment:', currentQcFulfillmentRef.current.id);
        onOrderComplete(currentQcFulfillmentRef.current.id);
      }

      // 觸發 PRINT_SUCCESS 事件，進入 COMPLETED 狀態
      triggerQcFlowEvent(QCFlowEvents.PRINT_SUCCESS, {
        jobId: printJobId,
        data: resultData
      });

      // 清除 job ID
      setPrintJobId(null);
    }, [printJobId, triggerQcFlowEvent, onOrderComplete]),

    onFailed: useCallback((data) => {
      console.error('[QcShipmentContext] Print job failed:', data);
      // 先快照並清除 job id：彈窗為 async，期間不應再被 notification 重複觸發。
      const jobId = printJobId;
      setPrintJobId(null);

      const rawError = data.error || data.message;
      const interpolations = translateErrorInterpolations(i18n.t.bind(i18n), data.interpolations);
      const translatedError = rawError
        ? i18n.t(`common.error.${rawError}`, { ...interpolations, defaultValue: rawError })
        : i18n.t('operator.qc.print_label.unknown_error');
      const fulfillment = currentQcFulfillmentRef.current;

      // 取消 / 結單失敗的共用收尾：顯示錯誤、同步 Alert、回到 AWAITING_RESCAN 可重掃重印。
      const fallbackToRescan = () => {
        message.error(`${i18n.t('operator.qc.print_label.shipment_failed')}: ${translatedError}`);
        if (onPrintError) onPrintError(rawError || 'shipment_failed', interpolations);
        triggerQcFlowEvent(QCFlowEvents.PRINT_FAILED_OR_CANCELED, { jobId, error: rawError });
      };

      // 列印失敗 → 詢問是否仍要結單（無標籤強制出貨）。
      // 確認：帶 confirmWithoutLabel 結單，成功則推進到 COMPLETED；取消：回到可重掃狀態。
      Modal.confirm({
        title: i18n.t('operator.qc.print_label.confirm_ship_without_label_title'),
        content: (
          <div>
            {/* 先讓操作員看到「為什麼印失敗」，再問要不要無標籤強制結單 */}
            <div style={{ marginBottom: 8, color: '#cf1322' }}>
              {i18n.t('operator.qc.print_label.shipment_failed')}：{translatedError}
            </div>
            <div>{i18n.t('operator.qc.print_label.confirm_ship_without_label_content')}</div>
          </div>
        ),
        okText: i18n.t('operator.qc.print_label.confirm_ship_without_label_ok'),
        okButtonProps: { danger: true },
        cancelText: i18n.t('common.button.cancel'),
        onOk: async () => {
          if (fulfillment?.id && onOrderComplete) {
            const result = await onOrderComplete(fulfillment.id, { confirmWithoutLabel: true });
            if (result?.success) {
              triggerQcFlowEvent(QCFlowEvents.PRINT_SUCCESS, { jobId });
              return;
            }
          }
          // 結單仍失敗（例如訂單被 hold / 取消）→ 回到可重掃狀態，錯誤已由 onOrderComplete 顯示。
          fallbackToRescan();
        },
        onCancel: fallbackToRescan,
      });
    }, [printJobId, triggerQcFlowEvent, onPrintError, onOrderComplete]),

    onProgress: useCallback((data) => {
      // 進度更新
      console.log('[QcShipmentContext] Print job progress:', data.message, data.progress);
    }, [])
  });

  // 重置狀態
  const resetShippingState = useCallback(() => {
    setPrintJobId(null);
    currentQcFulfillmentRef.current = null;
  }, []);

  // 設置當前出貨單 ref（用於 print job 完成時）
  const setCurrentQcFulfillmentRef = useCallback((fulfillment) => {
    currentQcFulfillmentRef.current = fulfillment;
  }, []);

  const value = {
    printJobId,
    setPrintJobId,
    resetShippingState,
    setCurrentQcOrderRef: setCurrentQcFulfillmentRef,
    setCurrentQcFulfillmentRef,
  };

  return (
    <QcShipmentContext.Provider value={value}>
      {children}
    </QcShipmentContext.Provider>
  );
};

export const useQcShipping = () => {
  const context = useContext(QcShipmentContext);
  if (!context) {
    throw new Error('useQcShipping must be used within a QcShipmentProvider');
  }
  return context;
};

export default QcShipmentContext;
